// Validation client-side des dispatch tasks pour eviter les rejets cryptiques 400/422 du
// backend. Source de verite : worlds-metadata.json (audit backend SSM 2026-05-27) qui aplatit
// la matrice `fleet × world × waypoint × action` deja resolue cote backend.
//
// Pourquoi on lit la metadata plutot que /api/building_map ? Parce que ce dernier strip les
// semantic flags (is_charger, is_cleaning_zone, pickup_dispenser, dropoff_ingestor) a la
// serialisation Pydantic cote rmf-web (cf doc dispatch-rmf-finding-1 backend). Sans ces flags
// l'UI ne peut pas distinguer un charger d'un waypoint regulier. La metadata est donc le
// workaround officiel.

import type { DispatchTaskRequest } from '$lib/api/backend-client';
import {
  getActionsForFleetOnWorld,
  getAllCategoriesForWorld,
  getCleaningZonesForFleetOnWorld,
  getAllCleaningZonesForWorld
} from './worlds-metadata';
import type { MapName } from './maps';

/**
 * Resultat de validation : ok=true ou ok=false avec un message localise-able cote UI.
 * On evite d'utiliser des Error throw pour ne pas force le caller a un try/catch — la UI
 * affiche juste le message d'erreur inline.
 */
export type ValidationResult = { ok: true } | { ok: false; error: string };

/**
 * Valide qu'un payload de dispatch est compatible avec le world (et la fleet si specifiee).
 * La metadata garantit deja que les options exposees a l'UI sont valides, donc cette
 * validation sert surtout de defense ceinture-bretelle (et pour les minima patrol).
 *
 * `fleetName` null = mode auto-dispatch : RMF Core arbitre. Dans ce cas on valide contre
 * l'union des capacites toutes-fleets-confondues du world.
 */
export function validateForCategory(
  worldName: MapName,
  fleetName: string | null,
  request: DispatchTaskRequest
): ValidationResult {
  // Mode auto : valide contre l'union des fleets dispo sur ce world.
  if (fleetName === null) {
    const actions = getAllCategoriesForWorld(worldName);
    if (actions.length === 0) {
      return { ok: false, error: `World "${worldName}" sans fleet dispatchable` };
    }
    if (!actions.includes(request.category)) {
      return {
        ok: false,
        error: `Aucune fleet ne supporte "${request.category}" sur ce world`
      };
    }
  } else {
    const actions = getActionsForFleetOnWorld(worldName, fleetName);
    if (actions.length === 0) {
      return { ok: false, error: `Fleet "${fleetName}" inconnue sur le world "${worldName}"` };
    }
    if (!actions.includes(request.category)) {
      return {
        ok: false,
        error: `Fleet "${fleetName}" ne supporte pas "${request.category}" sur ce world`
      };
    }
  }

  if (request.category === 'patrol') {
    if (request.places.length < 2) {
      return { ok: false, error: 'Patrol exige au moins 2 waypoints' };
    }
    if (request.rounds <= 0) {
      return { ok: false, error: 'Le nombre de tours doit etre > 0' };
    }
  }
  if (request.category === 'delivery') {
    if (request.pickup.place === request.dropoff.place) {
      return { ok: false, error: 'Pickup et dropoff doivent etre des waypoints differents' };
    }
  }
  if (request.category === 'clean') {
    const zones =
      fleetName === null
        ? getAllCleaningZonesForWorld(worldName)
        : getCleaningZonesForFleetOnWorld(worldName, fleetName);
    if (!zones.includes(request.zone)) {
      const where = fleetName ?? 'aucune fleet';
      return {
        ok: false,
        error: `Zone "${request.zone}" hors action_paths de "${where}" sur "${worldName}"`
      };
    }
  }
  return { ok: true };
}
